import React from 'react';
import { Keyboard, Tv, Play, Search, X, Volume2, Maximize, RotateCcw, RotateCw, Sparkles, Navigation } from 'lucide-react';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isTv?: boolean;
}

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
  isTv = false
}) => {
  if (!isOpen) return null;

  const tvRemoteShortcuts = [
    { key: '▲ ▼ ◄ ► (D-Pad)', action: 'Navegar i seleccionar qualsevol element o fila del catàleg' },
    { key: 'OK / Select / Enter', action: 'Obrir fitxa o reproduir el capítol seleccionat' },
    { key: 'Enrere / Back / Esc', action: 'Tancar finestres, sortir del reproductor o tornar a l\'inici' },
    { key: 'Play / Pause', action: 'Pausar o reprendre la reproducció del vídeo' },
    { key: 'Fletxa Dreta (►)', action: 'Avançar 10 segons al reproductor' },
    { key: 'Fletxa Esquerra (◄)', action: 'Retrocedir 10 segons al reproductor' },
    { key: 'Fletxes Amunt / Avall', action: 'Pujar / Baixar volum del reproductor' }
  ];

  const pcShortcuts = [
    { key: 'Espai / K', action: 'Reproduir / Pausar el vídeo' },
    { key: 'F', action: 'Pantalla completa (Fullscreen)' },
    { key: 'M', action: 'Silenciar / Activar àudio (Mute)' },
    { key: '◄ / ►', action: 'Avançar o retrocedir 10 segons' },
    { key: '▲ / ▼', action: 'Ajustar el volum del vídeo (0-100%)' },
    { key: '/', action: 'Obrir i enfocar el cercador d\'animes' },
    { key: 'Esc', action: 'Tancar finestres emergents o sortir de pantalla completa' }
  ];

  const mobileGestures = [
    { key: 'Doble toc a la dreta', action: 'Avançar +10 segons de vídeo ràpidament' },
    { key: 'Doble toc a l\'esquerra', action: 'Retrocedir -10 segons de vídeo' },
    { key: 'Un sol toc a la pantalla', action: 'Mostrar / Amagar els controls de reproducció' },
    { key: 'Glissem horitzontal', action: 'Desplaçar els carrusels d\'animes amb inèrcia suau' }
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0e121d] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/30">
              <Keyboard className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-display font-bold text-xl text-white">
                Guia de Controls & Comandament
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Dreceres per a Comandament TV (Android/Fire TV), Teclat de PC i Gestos Tàctils.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Section 1: TV Remote */}
        <div className="space-y-3">
          <h4 className="font-display font-bold text-sm text-amber-400 flex items-center gap-2">
            <Tv className="w-4 h-4" />
            <span>Comandament a Distància (Android TV & Fire TV Stick)</span>
          </h4>
          <div className="grid grid-cols-1 gap-2">
            {tvRemoteShortcuts.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between p-2.5 rounded-xl bg-[#131724] border border-white/5 text-xs">
                <span className="font-semibold text-slate-300">{s.action}</span>
                <span className="font-mono font-bold px-2 py-1 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-300">
                  {s.key}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 2: PC Shortcuts */}
        <div className="space-y-3 pt-2">
          <h4 className="font-display font-bold text-sm text-cyan-400 flex items-center gap-2">
            <Keyboard className="w-4 h-4" />
            <span>Teclat d'Ordinador (PC & Portàtil)</span>
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pcShortcuts.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between p-2.5 rounded-xl bg-[#131724] border border-white/5 text-xs">
                <span className="font-medium text-slate-300">{s.action}</span>
                <span className="font-mono font-bold px-2 py-1 rounded-lg bg-white/10 border border-white/10 text-white shrink-0 ml-2">
                  {s.key}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Mobile Touch Gestures */}
        <div className="space-y-3 pt-2">
          <h4 className="font-display font-bold text-sm text-emerald-400 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>Gestos Tàctils (Mòbil & Tauleta)</span>
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {mobileGestures.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between p-2.5 rounded-xl bg-[#131724] border border-white/5 text-xs">
                <span className="font-medium text-slate-300">{s.action}</span>
                <span className="font-mono text-[11px] font-bold px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 shrink-0 ml-2">
                  {s.key}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shadow-md transition-all"
          >
            Tancar
          </button>
        </div>

      </div>
    </div>
  );
};
